<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1>Evaluación de desempeño Anual</h1>
      </v-col>
      <v-col v-if="loadingPersonal" cols="12" algin="center">
        <div class="d-flex flex-column align-center">
          <v-progress-circular indeterminate color="accent" size="32"></v-progress-circular>
          <h4 class="mt-4">Cargando información de personal...</h4>
        </div>
      </v-col>
      <template v-else>
        <v-col cols="12">
          <DatosGeneralesForm
            :personal="personalEvaluado"
            :tipoEvaluacion.sync="tipoEvaluacion"
            :fechaAplicacion.sync="fechaAplicacion"
            :idEvaluador.sync="idEvaluador"
          />
        </v-col>
        <v-col v-if="funcionesStatus === ACTION_STATUS.LOADING" cols="12">
          <div class="d-flex flex-column align-center">
            <v-progress-circular indeterminate color="accent" size="32"></v-progress-circular>
            <h4 class="mt-4">Cargando listado de funciones...</h4>
          </div>
        </v-col>
        <v-col v-if="funcionesStatus === ACTION_STATUS.SUCCEEDED" cols="12">
          <CumplimientoFuncionesForm
            v-for="rubro in rubrosWithObjetivos"
            :key="rubro.id"
            :rubro="rubro"
            :reactivos.sync="reactivos"
            :frecuencias="frecuencias"
            :tipoEvaluacion="tipoEvaluacion"
          />
          <div class="mt-4 d-flex justify-end align-baseline">
            <BaseSelect
              v-model="estatusEvaluacion"
              label="Estatus"
              class="mr-2"
              style="max-width: 320px"
              :items="estatusOptions"
            />
          </div>
          <div class="mt-4 d-flex justify-end align-baseline">
            <TextButton
              :loading="creatingEvaluacion"
              :disabled="!allReactivosAreFilled || creatingEvaluacion"
              @click="saveEvaluacion"
            >
              Guardar evaluación
            </TextButton>
          </div>
        </v-col>
      </template>
    </v-row>
  </v-container>
</template>

<script>
import DatosGeneralesForm from '@src/components/desarrollo_policial/evaluacion-anual/DatosGeneralesForm.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import CumplimientoFuncionesForm from '@src/components/desarrollo_policial/evaluacion-anual/CumplimientoFuncionesForm.vue'
import EvaluacionAnualApiRepository from '@src/modules/evaluacion-anual/infrastructure/EvaluacionAnualApiRepository'
import GetRubros from '@src/modules/evaluacion-anual/application/GetRubros'
import RUBRO_OBJETIVO_EVACOM from '@src/modules/evaluacion-anual/domain/RUBRO_OBJETIVO_EVACOM'
import RUBRO_OBJETIVO_EVACOM_MANDOS from '@src/modules/evaluacion-anual/domain/RUBRO_OBJETIVO_EVACOM_MANDOS'
import GetReactivos from '@src/modules/evaluacion-anual/application/GetReactivos'
import GetFrecuencias from '@src/modules/evaluacion-anual/application/GetFrecuencias'
import { ACTION_STATUS } from '@src/const'
import TextButton from '@src/components/base/TextButton.vue'
import BaseSelect from '@src/components/base/BaseSelect.vue'
import CreateEvaluacionAnual from '@src/modules/evaluacion-anual/application/CreateEvaluacionAnual'
import { TIPOS_EVALUACION_ANUAL } from '@src/modules/evaluacion-anual/domain/TIPOS_EVALUACION_ANUAL'

export default {
  components: {
    DatosGeneralesForm,
    CumplimientoFuncionesForm,
    TextButton,
    BaseSelect,
  },

  props: {
    idPersonalEvaluado: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      ACTION_STATUS,
      loadingPersonal: true,
      tipoEvaluacion: null,
      fechaAplicacion: '',
      idEvaluador: null,
      rubros: [],
      reactivos: [],
      frecuencias: [],
      funcionesStatus: ACTION_STATUS.IDLE,
      estatusEvaluacion: null,
      estatusOptions: [
        {
          id: 2,
          title: 'Evaluación en Proceso',
        },
        {
          id: 3,
          title: 'Evaluación Finalizada',
        },
        {
          id: 1,
          title: 'Evaluación Pendiente',
        },
      ],
      creatingEvaluacion: false,
    }
  },

  computed: {
    ...mapGetters('personal', { personalEvaluado: 'getPersonalSingle' }),

    rubrosWithObjetivos: function () {
      return this.rubros.map((rubro) => {
        const objetivo =
          this.tipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM
            ? RUBRO_OBJETIVO_EVACOM[rubro.id]
            : RUBRO_OBJETIVO_EVACOM_MANDOS[rubro.id]

        return { ...rubro, objetivo: objetivo ?? 'N/A' }
      })
    },

    allReactivosAreFilled: function () {
      return this.reactivos.every(({ calificacion }) => !!calificacion)
    },
  },

  watch: {
    async tipoEvaluacion(newValue, oldValue) {
      if (newValue !== oldValue) {
        try {
          this.funcionesStatus = ACTION_STATUS.LOADING
          this.reactivos = await new GetReactivos(EvaluacionAnualApiRepository).execute(newValue)
          this.funcionesStatus = ACTION_STATUS.SUCCEEDED
        } catch (error) {
          this.funcionesStatus = ACTION_STATUS.FAILED
        }
      }
    },
  },

  async mounted() {
    this.findPersonal()
    this.rubros = await new GetRubros(EvaluacionAnualApiRepository).execute()
    this.frecuencias = await new GetFrecuencias(EvaluacionAnualApiRepository).execute()
  },

  methods: {
    ...mapActions('personal', ['fetchPersonalById']),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),

    async findPersonal() {
      try {
        await this.fetchPersonalById(this.idPersonalEvaluado)
      } catch (error) {
        console.error('Error fetching personal data:', error)
      } finally {
        this.loadingPersonal = false
      }
    },

    async saveEvaluacion() {
      try {
        this.creatingEvaluacion = true

        const usecase = new CreateEvaluacionAnual(EvaluacionAnualApiRepository)
        await usecase.execute({
          idPersonalEvaluado: this.idPersonalEvaluado,
          idPersonalEvaluador: this.idEvaluador,
          estatus: this.estatusEvaluacion,
          fecha: this.fechaAplicacion,
          reactivos: this.reactivos,
          tipoEvaluacion: this.tipoEvaluacion,
        })

        this.SET_SNACKBAR_MESSAGE('Evaluación guardada correctamente.')
        this.$router.push('/desarrollo_profesional/personal')
      } catch (error) {
        console.error('Error saving evaluacion:', error)
        this.SET_SNACKBAR_MESSAGE('No fue posible guardar la evaluación. Intente nuevamente.')
      } finally {
        this.creatingEvaluacion = false
      }
    },
  },
}
</script>
